import React, { Component, useState } from "react"
import { getSectionInfoACT, saveAllComputerBasedTestAnswers, saveHighlightForQuestion, ACT_SECTION_MODULE_KEYS, findNextModuleNameKeyForACT, saveComputerBasedTestAnswerACT } from "../../../../services/utils/practice-test-util"
import { CountdownTimerACT } from "../../../../components/CountdownTimer"
import Calculator from "../../../../components/Calculator"
import {LineReaderACT} from "../../../../components/LineReader"
import QuestionACT from "./QuestionACT"
import { CDN_PUB_URL, NA } from "../../../../constants"
import ActDropdown, { ActFilterDropdown } from "./ActDropdown"
import ActButton from "./ActButton"
import SplitPanel from "../../../../components/SplitPanel"
import { HighlightControlsACT } from "../../../../components/Highlighter"
import { IAnsweredQuestion } from "../../../../services/practice-test-scoring/practice-test-summary"
import InformationalContent from "./InformationalContent"
import { MathKatex2 } from "../../../../components/MathKatex"
import { prepareContentWithCDNImages } from "../../../../services/utils/html-util"
import "./ComputerTestACT.css"

interface Props {
    userInfo: any
    testDetails: any
    onNext: () => void
    onTestExit: () => void
    currentModuleNameKey: string
    onPageUnmount?: () => void
}

interface State {
    currentQuestionIndex: number
    maxQuestions: number
    disableBack: boolean
    disableNext: boolean
    calculatorVisible: boolean
    referenceVisible: boolean
    directionsVisible: boolean
    lineReaderVisible: boolean
    highligthInfoVisible: boolean
    answeredQuestions: any[]
    currentQuestions: any[]
    computerTestCapabilities: any
    remainingTimeInSeconds: number
    startTimer: boolean
    miniReviewPageVisible: boolean
    mainReviewPageVisible: boolean
    questionInfoButtonVisible: boolean
    showSplitPanel: boolean
    showEndOfTestModal: boolean
    allowAnswerEliminator: boolean
    allowAnswerMasking: boolean
    highlighterVisible: boolean
    highlighterEnabled: boolean
    currentInformationalIndex: number
}

export const NUMBER_OF_INFORMATIONAL_PAGES = 2

/**
 * Computer-based Virtual ACT Practice Test (AKA ACT Digital Test).
 * This component emulates the official ACT practice test desktop application as much as possible.
 * 
 * Notes:
 *  + Timer starts right away
 *  + Directions for the test is visible for the user (timer still continues)
 *  + There are components such as Calculator and Reference available based on the test type
 */
export default class ComputerTestACT extends Component<Props, State> {
    static instance: ComputerTestACT
    static TOOLS_MAP = {
        answerEliminator: {
            label: "Answer Eliminator", 
            command: "toggleAnswerEliminator", 
            selected: false 
        },
        answerMasking: {
            label: "Answer Masking", 
            command: "toggleAnswerMasking", 
            selected: false 
        },
        highlighter: {
            label: "Highlighter", 
            command: "toggleHighlighter", 
            selected: false 
        },
        calculator: {
            label: "Calculator", 
            command: "toggleCalculator", 
            selected: false 
        },
        linereader: {
            label: "Line Reader", 
            command: "toggleLineReader", 
            selected: false 
        },
    }
    static clearAllToolSelections() {
        let map = ComputerTestACT.TOOLS_MAP
        let keys = Object.keys(map)
        keys.forEach(key => map[key].selected = false)
    }
    static reportedRemainingTestTimeInSeconds = -1

    constructor(props) {
        super(props)
        this.state = {
            currentQuestionIndex: 0,
            currentQuestions: [],
            maxQuestions: 0,
            disableBack: true,
            disableNext: false,
            directionsVisible: true, // Initially visible
            calculatorVisible: false,
            referenceVisible: false,
            lineReaderVisible: false,
            highligthInfoVisible: false,
            miniReviewPageVisible: false,
            mainReviewPageVisible: false,
            questionInfoButtonVisible: true,
            answeredQuestions: [],
            computerTestCapabilities: {},
            remainingTimeInSeconds: -1,
            startTimer: false,
            showSplitPanel: false,
            showEndOfTestModal: false,
            allowAnswerEliminator: false,
            allowAnswerMasking: false,
            highlighterVisible: false,
            highlighterEnabled: false,
            currentInformationalIndex: 0
        }
    }

    componentDidMount() {
        window.addEventListener("beforeunload", this.handlePageUnload)
        window.addEventListener("popstate", this.handlePopstate)
        ComputerTestACT.instance = this
        ComputerTestACT.clearAllToolSelections()
        this.initialize()
    }

    componentWillUnmount() {
        window.removeEventListener("beforeunload", this.handlePageUnload)
        setTimeout(() => {
            window.removeEventListener("popstate", this.handlePopstate)
        }, 500)

        let {onPageUnmount} = this.props

        if (onPageUnmount) {
            onPageUnmount()
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>) {
        let { testDetails } = this.props
        let { remainingTimeInSeconds } = testDetails
        let { testDetails: prevTestDetails } = prevProps
        let { remainingTimeInSeconds: prevRemainingTimeInSeconds } = prevTestDetails

        if ((prevProps.currentModuleNameKey !== this.props.currentModuleNameKey) || (prevRemainingTimeInSeconds !== remainingTimeInSeconds)) {
            this.initialize()
        }
    }

    initialize = () => {
        let {testDetails, currentModuleNameKey} = this.props
        let {questions, remainingTimeInSeconds, testTimeIndicator, currentQuestionIndex} = testDetails

        currentModuleNameKey = currentModuleNameKey ? currentModuleNameKey: ACT_SECTION_MODULE_KEYS.ENGLISH_MODULE

        let {computerTestCapabilities} = getSectionInfoACT(currentModuleNameKey, testDetails)
        let questionsForModule = questions[currentModuleNameKey]
        let maxQuestions = questionsForModule.length
        let duration = computerTestCapabilities.testDurationInSeconds[testTimeIndicator]
        let {currentInformationalIndex} = this.state

        if (currentQuestionIndex > 0) {
            currentInformationalIndex = NUMBER_OF_INFORMATIONAL_PAGES
        }

        // Note that the value -1 for remainingTimeInSeconds is the sentinel value for the remaining time!
        // If the test already started (i.e. remainingTimeInSeconds !== -1) then we need to set the startTimer to true.
        // Also, unlike SAT, the ACT timer starts as soon as the Instructions page is shown (NOT at the Information page).
        let startTimer = remainingTimeInSeconds > -1 && currentInformationalIndex > 0 ? true: false

        remainingTimeInSeconds = remainingTimeInSeconds !== -1 ? remainingTimeInSeconds: duration
        ComputerTestACT.reportedRemainingTestTimeInSeconds = remainingTimeInSeconds

        this.setState({
            maxQuestions, 
            currentQuestions: questionsForModule, 
            computerTestCapabilities, 
            remainingTimeInSeconds, 
            currentQuestionIndex,
            disableBack: false,
            currentInformationalIndex,
            startTimer,
        })
    }

    static showEndOfTestModal() {
        if (!ComputerTestACT.instance) return

        ComputerTestACT.instance.setState({showEndOfTestModal: true})
    }

    // Page unload detected!
    handlePageUnload = () => {
        this.submitTestDetails(false)
    }

    // Browser back button navigation detected!
    handlePopstate = () => {
        this.submitTestDetails(false)
    }

    handleBack = () => {
        let {currentInformationalIndex} = this.state
        let {currentQuestionIndex, disableBack, mainReviewPageVisible} = this.state

        if (currentQuestionIndex <= 0) {
            currentInformationalIndex--

            if (currentInformationalIndex <= 0) {
                currentInformationalIndex = 0
                disableBack = true
            }

            if (currentInformationalIndex < NUMBER_OF_INFORMATIONAL_PAGES) {
                this.setState({currentInformationalIndex, disableBack})
                return
            }
        }

        if (mainReviewPageVisible) {
            this.setState({mainReviewPageVisible: false, questionInfoButtonVisible: true})
            return
        }

        --currentQuestionIndex

        if (currentQuestionIndex <= 0) {
            currentQuestionIndex = 0
        }

        this.setState({currentQuestionIndex, disableBack, disableNext: false})
    }

    handleNext = () => {
        let {currentInformationalIndex, currentQuestionIndex, maxQuestions, disableNext, mainReviewPageVisible} = this.state
        let {onNext} = this.props

        currentInformationalIndex++

        if (currentInformationalIndex <= NUMBER_OF_INFORMATIONAL_PAGES) {
            if (currentInformationalIndex > 0) {
                // This is the part we also need to set the startTimer to true!
                // because the ACT timer doesn't start right away, it starts in the Instructions page!
                this.setState({startTimer: true, currentInformationalIndex})
            }
            else {
                this.setState({currentInformationalIndex})
            }
            return
        }

        ++currentQuestionIndex

        if (mainReviewPageVisible) {
            this.submitModuleCompletion()
            onNext()
        }

        if (currentQuestionIndex >= maxQuestions) {
            this.setState({mainReviewPageVisible: true})
        }
        else {
            this.setState({currentQuestionIndex, disableBack: false, disableNext})
        }
    }

    toggleCalculatorVisibility = () => {
        let {calculatorVisible} = this.state
        calculatorVisible = !calculatorVisible
        this.setState({calculatorVisible})
        ComputerTestACT.TOOLS_MAP.calculator.selected = calculatorVisible
    }

    toggleLineReader = () => {
        let {lineReaderVisible} = this.state
        lineReaderVisible = !lineReaderVisible
        this.setState({lineReaderVisible})
        ComputerTestACT.TOOLS_MAP.linereader.selected = lineReaderVisible
    }

    toggleAnswerEliminator = () => {
        let {allowAnswerEliminator} = this.state
        allowAnswerEliminator = !allowAnswerEliminator
        this.setState({allowAnswerEliminator})
        ComputerTestACT.TOOLS_MAP.answerEliminator.selected = allowAnswerEliminator
    }

    toggleAnswerMasking  = () => {
        let {allowAnswerMasking} = this.state
        allowAnswerMasking = !allowAnswerMasking
        this.setState({allowAnswerMasking})
        ComputerTestACT.TOOLS_MAP.answerMasking.selected = allowAnswerMasking
    }

    toggleHighlighter = () => {
        let {highlighterVisible} = this.state
        highlighterVisible = !highlighterVisible
        this.setState({highlighterVisible, highlighterEnabled: highlighterVisible})
        ComputerTestACT.TOOLS_MAP.highlighter.selected = highlighterVisible
    }

    toggleDirectionsVisibilty = () => {
        let {directionsVisible} = this.state
        this.setState({directionsVisible: !directionsVisible})
    }

    toggleHighlight = (over: boolean) => {
        this.setState({highligthInfoVisible: over})
    }

    closeCalculator = () => {
        this.setState({calculatorVisible: false})
        ComputerTestACT.TOOLS_MAP.calculator.selected = false
    }

    closeReference = () => {
        this.setState({referenceVisible: false})
    }

    openDirections = () => {
        this.setState({directionsVisible: true})
    }

    closeDirections = () => {
        this.setState({directionsVisible: false})
    }

    closeLineReader = () => {
        this.setState({lineReaderVisible: false})
        ComputerTestACT.TOOLS_MAP.linereader.selected = false
    }

    closeHighlighter = () => {
        this.setState({highlighterVisible: false, highlighterEnabled: false})
        ComputerTestACT.TOOLS_MAP.highlighter.selected = false
    }

    showHelpForLineReader = () => {
        // OT: TODO - for the next round
        // console.log("OT: Show help modal for the Line Reader!")
    }

    showHelpForHighlighter = () => {
        // OT: TODO - for the next round
        // console.log("OT: Show help modal for the Highlighter!")
    }

    handleTimerTick = (remainingTimeInSeconds) => {
        ComputerTestACT.reportedRemainingTestTimeInSeconds = remainingTimeInSeconds
    }

    handleTimeFinish = () => {
        let {onNext} = this.props

        this.submitTestDetails(true)

        if (onNext) {
            onNext()
        }
    }

    handleTestExit = () => {
        let {onTestExit} = this.props

        this.submitTestDetails(false)

        if (onTestExit) {
            onTestExit()
        }
    }

    handleNextSection = () => {
        let {onNext} = this.props
        this.setState({showEndOfTestModal: false})
        this.submitTestDetails(true)
        if (onNext) {
            onNext()
        }
    }

    submitTestDetails = (dueToTimeOutOrNextSectionAction: boolean) => {
        let {testDetails, currentModuleNameKey} = this.props
        let {currentQuestionIndex} = this.state

        testDetails.remainingTimeInSeconds = ComputerTestACT.reportedRemainingTestTimeInSeconds
        testDetails.currentQuestionIndex = currentQuestionIndex
        testDetails.currentModuleNameKey = currentModuleNameKey

        if (dueToTimeOutOrNextSectionAction) {
            let nextModuleNameKey = findNextModuleNameKeyForACT(currentModuleNameKey)

            if (nextModuleNameKey !== NA) {
                testDetails.currentModuleNameKey = nextModuleNameKey
            }
            else {
                testDetails.currentModuleNameKey = ""
                testDetails.completedTime = new Date().toISOString()
            }
            testDetails.remainingTimeInSeconds = -1
            testDetails.currentQuestionIndex = 0
        }

        saveAllComputerBasedTestAnswers(testDetails)
    }

    submitModuleCompletion = () => {
        let {testDetails, currentModuleNameKey} = this.props
        let nextModuleNameKey = findNextModuleNameKeyForACT(currentModuleNameKey)

        if (nextModuleNameKey !== NA) {
            testDetails.currentModuleNameKey = nextModuleNameKey
        }
        else {
            testDetails.currentModuleNameKey = ""
            testDetails.completedTime = new Date().toISOString()
        }
        testDetails.remainingTimeInSeconds = -1
        testDetails.currentQuestionIndex = 0
        saveAllComputerBasedTestAnswers(testDetails)
    }

    submitAnswer = (answeredQuestion, fillIn=false) => {
        let {currentQuestions} = this.state
        let {testDetails, currentModuleNameKey} = this.props
        let {testName} = testDetails

        saveComputerBasedTestAnswerACT(testName, currentModuleNameKey, answeredQuestion)
        this.setState({currentQuestions})
    }

    handleAnswer = (question, answer: string) => {
        question.userSubmittedAnswer = answer
        this.submitAnswer(question)
    }

    handleFillInAnswer = (question, fillInAnswer: string,) => {
        question.fillInAnswer = fillInAnswer
        this.submitAnswer(question, true)
    }

    handleBookmark = (question) => {
        question.bookmarked = !question.bookmarked // Toggle bookmark
        this.submitAnswer(question)
    }

    handleHighlight = (question) => {
        saveHighlightForQuestion(question)
    }

    handleHighlighterEdit = () => {
        let {highlighterEnabled} = this.state
        highlighterEnabled = !highlighterEnabled
        this.setState({highlighterEnabled})
    }

    handleHighlighterClear = () => {
        let {currentQuestions, currentQuestionIndex} = this.state
        if (currentQuestions && currentQuestionIndex !== -1) {
            let question = currentQuestions[currentQuestionIndex]
            question.highlightedHTMLLeft = ""
            question.highlightedHTMLRight = ""
            this.setState({currentQuestions})
        }
    }

    onToggleMiniReview = () => {
        this.setState({miniReviewPageVisible: !this.state.miniReviewPageVisible})
    }

    closeMiniReview = () => {
        this.setState({miniReviewPageVisible: false})
    }

    gotoQuestion = (questionNumber) => {
        let questionIndex = questionNumber - 1 // 0-based index
        let disableBack = false
        this.setState({
            currentQuestionIndex: questionIndex, 
            currentInformationalIndex: 2,
            miniReviewPageVisible: false, 
            mainReviewPageVisible: false, 
            disableBack, 
            questionInfoButtonVisible: true,
        })
    }

    gotoInformation = (item) => {
        let {clickable} = item

        if (!clickable) return

        this.setState({
            currentInformationalIndex: 1,
            currentQuestionIndex: 0, 
        })
    }

    showMainReviewPage = () => {
        this.setState({mainReviewPageVisible: true, miniReviewPageVisible: false, questionInfoButtonVisible: false})
    }

    handleCommand = (command: string) => {
        switch (command) {
            case "toggleLineReader":
                this.toggleLineReader()
                break

            case "toggleCalculator":
                this.toggleCalculatorVisibility()
                break

            case "toggleAnswerEliminator":
                this.toggleAnswerEliminator()
                break

            case "toggleAnswerMasking":
                this.toggleAnswerMasking()
                break

            case "toggleHighlighter":
                this.toggleHighlighter()
                break

            case "":
                break
        }
    }

    render() {
        let {userInfo, testDetails, currentModuleNameKey} = this.props
        let {calculatorVisible, directionsVisible, referenceVisible, lineReaderVisible, highlighterVisible, highlighterEnabled, startTimer, remainingTimeInSeconds, miniReviewPageVisible, showSplitPanel, showEndOfTestModal} = this.state

        if (!testDetails) return null

        let isDirectionsOpen = directionsVisible
        let {firstName, lastName} = userInfo
        let {currentInformationalIndex, currentQuestionIndex, disableBack, disableNext, mainReviewPageVisible, allowAnswerEliminator, allowAnswerMasking} = this.state
        let questionsForModule = testDetails.questions[currentModuleNameKey]
        let questionsAnswered = questionsForModule.filter(q => !!q.userSubmittedAnswer).length
        let data = { userName: `${firstName} ${lastName}`, questionsAnswered, questionCurrent: currentQuestionIndex + 1, questionMax: questionsForModule.length}
        let calculatorClass = calculatorVisible ? "" : "hide"
        let lineReaderClass = lineReaderVisible ? "" : "hide"
        let highlighterClass = highlighterVisible ? "" : "hide"
        let {sectionTitle, testTitle, computerTestCapabilities} = getSectionInfoACT(currentModuleNameKey, testDetails)
        let currentQuestions = questionsForModule.map(q => ({ ...q }))

        if (mainReviewPageVisible) {
            disableBack = false
        }

        return (
            <div className="computer-test-act eact-wrapper">
                <SectionTop currentModuleNameKey={currentModuleNameKey} 
                    sectionTitle={sectionTitle}
                    testTitle={testTitle}
                    computerTestCapabilities={computerTestCapabilities}
                    remainingTimeInSeconds={remainingTimeInSeconds}
                    startTimer={startTimer}
                    onToggleDirections={this.toggleDirectionsVisibilty}
                    onToggleCalculator={this.toggleCalculatorVisibility}
                    onToggleLineReader={this.toggleLineReader}
                    onToggleHighlight={this.toggleHighlight}
                    onTestExit={this.handleTestExit}
                    onTimerTick={this.handleTimerTick}
                    onTimeFinish={this.handleTimeFinish}
                    isDirectionsOpen={isDirectionsOpen}
                    data={data}
                    onCommand={this.handleCommand}
                />
                <SectionMain>
                    {showSplitPanel ?
                        <SplitPanel allowResize={true} dividerWidth={3}
                            panelLeft={
                                <SectionQuestionArea
                                    currentInformationalIndex={currentInformationalIndex}
                                    questions={questionsForModule} 
                                    questionIndex={currentQuestionIndex}
                                    computerTestCapabilities={computerTestCapabilities}
                                    highlighterEnabled={highlighterEnabled}
                                    onAnswer={this.handleAnswer}
                                    onBookmark={this.handleBookmark}
                                    onHighlight={this.handleHighlight}
                                    onGotoQuestion={this.gotoQuestion}
                                    mainReviewPageVisible={mainReviewPageVisible}
                                    onBack={this.handleBack}
                                    onNext={this.handleNext}
                                    disableBack={disableBack}
                                    disableNext={disableNext}
                                />
                            }
                            panelRight={
                                <SectionRight
                                    questions={currentQuestions}
                                    showSplitPanel={true}
                                    currentInformationalIndex={currentInformationalIndex}
                                    currentQuestionIndex={currentQuestionIndex}
                                    onGotoInformation={this.gotoInformation}
                                    onGotoQuestion={this.gotoQuestion}
                                    onToggleFilteredArea={() => this.setState({showSplitPanel: !showSplitPanel})}
                                />
                            }
                        />
                        :
                        <SectionMiddleContainer>
                            <SectionQuestionArea 
                                currentInformationalIndex={currentInformationalIndex}
                                questions={questionsForModule} 
                                questionIndex={currentQuestionIndex}
                                computerTestCapabilities={computerTestCapabilities}
                                highlighterEnabled={highlighterEnabled}
                                onAnswer={this.handleAnswer}
                                onBookmark={this.handleBookmark}
                                onHighlight={this.handleHighlight}
                                onGotoQuestion={this.gotoQuestion}
                                mainReviewPageVisible={mainReviewPageVisible}
                                onBack={this.handleBack}
                                onNext={this.handleNext}
                                disableBack={disableBack}
                                disableNext={disableNext}
                                allowAnswerEliminator={allowAnswerEliminator}
                                allowAnswerMasking={allowAnswerMasking}
                            />
                            <SectionRight
                                questions={currentQuestions}
                                currentInformationalIndex={currentInformationalIndex}
                                currentQuestionIndex={currentQuestionIndex}
                                onGotoInformation={this.gotoInformation}
                                onGotoQuestion={this.gotoQuestion}
                                showSplitPanel={false} 
                                onToggleFilteredArea={() => this.setState({showSplitPanel: !showSplitPanel})}
                            />
                        </SectionMiddleContainer>
                    }
                </SectionMain>

                <Calculator
                    title="Desmos Graphing Calculator"
                    config="ACT"
                    draggable={true} 
                    calculatorClass={calculatorClass} 
                    startMaximized={true}
                    showMaximizeButton={false}
                    showCenterIcon={false}
                    resizable={true}
                    onClose={this.closeCalculator} 
                />

                <HighlightControlsACT 
                    draggable={true}
                    customClass={`${highlighterClass}`}
                    onClose={this.closeHighlighter}
                    onHelp={this.showHelpForHighlighter}
                    onAllowEdit={this.handleHighlighterEdit}
                    onClear={this.handleHighlighterClear}
                />

                <LineReaderACT
                    draggable={true}
                    customClass={`${lineReaderClass} line-reader-transparent`}
                    onClose={this.closeLineReader}
                    onHelp={this.showHelpForLineReader}
                />

                { mainReviewPageVisible && 
                    <EndOfTestReview 
                        title="End Test Confirmation"
                        questions={currentQuestions} 
                        onClose={() => this.setState({mainReviewPageVisible: false})}
                        onNextSection={this.handleNextSection}
                    />
                }

                { showEndOfTestModal && 
                    <EndOfTestConfirmation
                        title="End Test Confirmation"
                        questions={questionsForModule}
                        onClose={() => this.setState({showEndOfTestModal: false})}
                        onEndTest={this.handleTestExit}
                        onNextSection={this.handleNextSection}>
                    </EndOfTestConfirmation>
                }

            </div>
        )
    }
}

//#region --- Helpers ---

function ActLogo() {
    let imgUrl = "/assets/images/icons/tests/act-logo-white.svg"
    return (
        <div className=" eact-col-logo">
            <img src={imgUrl} alt="ACT logo" className="act-logo" />
        </div>
    )
}

function IconTools() {
    let imgUrl = "/assets/images/icons/tests/act-tools.svg"
    return (
        <img src={imgUrl} alt="ACT tools icon" className="act-icon-tools" />
    )
}

function IconContrast() {
    let imgUrl = "/assets/images/icons/tests/act-computer.svg"
    return (
        <img src={imgUrl} alt="ACT contrast icon" className="act-icon-contrast" />
    )
}

function IconEyeOpen() {
    // let imgUrl = "/assets/images/icons/tests/act-eye-open.svg"
    return (
        <svg className="" width="17" height="17" xmlns='http://www.w3.org/2000/svg' viewBox='0 0 576 512'><path fill="currentColor" d='M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM432 256c0 79.5-64.5 144-144 144s-144-64.5-144-144s64.5-144 144-144s144 64.5 144 144zM288 192c0 35.3-28.7 64-64 64c-11.5 0-22.3-3-31.6-8.4c-.2 2.8-.4 5.5-.4 8.4c0 53 43 96 96 96s96-43 96-96s-43-96-96-96c-2.8 0-5.6 .1-8.4 .4c5.3 9.3 8.4 20.1 8.4 31.6z'/></svg>
    )
}

function IconEyeClosed() {
    // let imgUrl = "/assets/images/icons/tests/act-eye-closed.svg"
    return (
        <svg className="" width="18" height="18" xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 512'><path fill="currentColor" d='M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c5.2-11.8 8-24.8 8-38.5c0-53-43-96-96-96c-2.8 0-5.6 .1-8.4 .4c5.3 9.3 8.4 20.1 8.4 31.6c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zm223.1 298L373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5z'/></svg>
    )
}

function IconStop() {
    let imgUrl = "/assets/images/icons/tests/act-stop.svg"
    return (
        <svg width="18" height="18" className="act-icon-stop" fill="currentColor" xmlns='http://www.w3.org/2000/svg' viewBox='0 0 384 512'><path d='M0 128C0 92.7 28.7 64 64 64H320c35.3 0 64 28.7 64 64V384c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V128z'/></svg>
    )
}

function IconBars() {
    // let imgUrl = "/assets/images/icons/tests/act-bars.svg"
    return (
        <svg fill="currentColor" width="20" height="20" xmlns='http://www.w3.org/2000/svg' viewBox='0 0 576 512'><path d='M544 0c17.7 0 32 14.3 32 32V480c0 17.7-14.3 32-32 32s-32-14.3-32-32V32c0-17.7 14.3-32 32-32zM416 96c17.7 0 32 14.3 32 32V480c0 17.7-14.3 32-32 32s-32-14.3-32-32V128c0-17.7 14.3-32 32-32zM320 224V480c0 17.7-14.3 32-32 32s-32-14.3-32-32V224c0-17.7 14.3-32 32-32s32 14.3 32 32zM160 288c17.7 0 32 14.3 32 32V480c0 17.7-14.3 32-32 32s-32-14.3-32-32V320c0-17.7 14.3-32 32-32zM64 416v64c0 17.7-14.3 32-32 32s-32-14.3-32-32V416c0-17.7 14.3-32 32-32s32 14.3 32 32z'/></svg>
    )
}

function IconFilter() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="16" viewBox="0 0 512 512" fill="currentColor"><path d="M487.976 0H24.028C2.71 0-8.047 25.866 7.058 40.971L192 225.941V432c0 7.831 3.821 15.17 10.237 19.662l80 55.98C298.02 518.69 320 507.493 320 487.98V225.941l184.947-184.97C520.021 25.896 509.338 0 487.976 0z"/></svg>
    )
}

function ActTopCenterControls(props) {
    let toolsButtons = Object.keys(ComputerTestACT.TOOLS_MAP)
    let {onCommand, computerTestCapabilities} = props

    function handleCommand(command: string) {
        if (onCommand) {
            onCommand(command)
        }
    }

    function allowedInDropdown(toolItem) {
        let {label} = toolItem
        switch (label) {
            case "Calculator":
                return computerTestCapabilities.calculator

            case "Highlighter":
                return computerTestCapabilities.highlight
        }
        return true
    }


    return (
        <div className="col text-center eact-col-tools">
            <ActDropdown icon={<IconTools/>} label="TOOLS" >
                { toolsButtons.map(key => {
                    let item = ComputerTestACT.TOOLS_MAP[key]
                    let allowed = allowedInDropdown(item)
                    if (!allowed) return
                    return (
                        <button
                            key={key}
                            className="act-dropdown-item" 
                            onClick={() => handleCommand(item.command)}>
                            <span className="act-dropdown-item--ico"><img src="/assets/images/icons/v2/ico-check-tool.svg" alt="" width="16" height="16" className={item.selected ? "" : "disabled"}/></span>
                            {item.label}
                        </button>
                    )}
                )}
            </ActDropdown>
        </div>
    )
}


function FilteredQuestion({question, currentQuestionIndex, onClick}) {
    let {questionNumber, questionQualifier, userSubmittedAnswer, } = question
    let questionInfo = `Question ${questionNumber}`
    let isCurrentQuestion = questionNumber === (currentQuestionIndex + 1)
    let cardClass = `filtered-question-card ${isCurrentQuestion ? "current-question": ""}`

    if (isCurrentQuestion) {
        // We need to scrool up this cart to top!
        // console.log("OT: We need to scrool up this cart to top!", questionNumber)
    }

    return (
        <div className={cardClass}>
            <div className="question-header">
                <span className="question-title">
                    <SimpleBookmark question={question} />
                    {questionInfo}
                </span>
                { userSubmittedAnswer && <span className="answer-status">Answered</span> }
            </div>
            <div className="filtered-question-body" onClick={onClick}>
                <MathKatex2 mixedContent={questionQualifier} />
            </div>
        </div>
    )
}

function FilteredItem({item, question, informationClicked, currentInformationalIndex, currentQuestionIndex, onClick}) {
    let {id, type, title, text, questionNumber, answered, flagged, clickable} = item
    let questionInfo = title //`Question ${questionNumber}`
    let isCurrentQuestion = questionNumber === (currentQuestionIndex + 1)
    let cardClass = `filtered-question-card `
    let questionQualifier = text
    let highlightedCard = false

    if (type === "informational") {
        if (clickable && currentInformationalIndex === 1) {
            highlightedCard = true
            cardClass += "current-question"
        }
    }
    else if (type === "question" && currentInformationalIndex !== 1 && isCurrentQuestion) {
        highlightedCard = true
        cardClass += "current-question"
    }

    if (isCurrentQuestion) {
        // We need to scrool up this cart to top!
        // console.log("OT: We need to scrool up this cart to top!", questionNumber)
    }

    let notHighlightedAnswered = false
    let informational = !highlightedCard && type === "informational"
    let preparedQuestionQualifier = prepareContentWithCDNImages(CDN_PUB_URL, questionQualifier, "no-image")

    return (
        <div className={cardClass}>
            <div className="question-header">
                <span className="question-title">
                    <SimpleBookmark question={item} />
                    {questionInfo}
                </span>
                { answered && <span className="answer-status">Answered</span> }
            </div>
            <div className={`filtered-question-body ${informational ? "informational": ""} ${clickable ? "clickable": ""} ${notHighlightedAnswered ? "answered": ""}`} onClick={onClick}>
                <MathKatex2 mixedContent={preparedQuestionQualifier} />
            </div>
        </div>
    )
}

function SimpleBookmark(props) {
    let {question, onBookmark} = props
    let {flagged: bookmarked} = question
    let imgUrl = `/assets/images/icons/v2/${!bookmarked ? "ico-bookmark.svg" : "ico-bookmark-bright.svg"}`

    return (
        bookmarked ?
            <span className={`bookmark-icon ${bookmarked ? "bookmarked": ""}`}>
                <img src={imgUrl} width="14" height="19" alt="bookmark"/>
            </span>
            :
            <></>
    )
}

const optionsMap = {
    allquestions: {
        label: "ALL QUESTIONS",
        getQuestions: (questions) => questions,
        info: () => "All Questions",
    },
    flagged: {
        label: "FLAGGED",
        getQuestions: (questions) => questions.filter(q => q.bookmarked),
        info: (questions) =>  `Flagged (${questions.filter(q => q.bookmarked).length})`,
    },
    answered: {
        label: "ANSWERED",
        getQuestions: (questions) => questions.filter(q => q.userSubmittedAnswer && q.userSubmittedAnswer !== "?"),
        info: (questions) =>  `Answered (${questions.filter(q => q.userSubmittedAnswer && q.userSubmittedAnswer !== "?").length})`,
    },
    unanswered: {
        label: "UNANSWERED",
        getQuestions: (questions) => questions.filter(q => (!q.userSubmittedAnswer || q.userSubmittedAnswer === "?") && q.type !== "informational"),
        info: (questions) =>  `Unanswered (${questions.filter(q => !q.userSubmittedAnswer || q.userSubmittedAnswer === "?").length})`,
    },
}

// Informational items
const informationalItemsBySections = {
    englishAnswers: [
        {id: "ie1", type: "informational", title: "Information", text: "English Test Directions. The next screen in this section contains instructions about the English test...", clickable: false, answered: false, flagged: false},
        {id: "ie2", type: "informational", title: "Instructions", text: "50 questions in the passages that follow, certain words and phrases and underlines and highlighted...", clickable: true, answered: false, flagged: false},
    ],
    mathAnswers: [
        {id: "im1", type: "informational", title: "Information", text: "Math Test Directions. The next screen in this section contains instructions about the Mathemathics test...", clickable: false, answered: false, flagged: false},
        {id: "im2", type: "informational", title: "Instructions", text: "45 questions. Solve each problem, choose the correct answer, and select your answer. Do not linger...", clickable: true, answered: false, flagged: false},
    ],
    readingAnswers: [
        {id: "ir1", type: "informational",title: "Information", text: "Reading Test Directions. The next screen in this section contains instructions about the Reading test...", clickable: false, answered: false, flagged: false},
        {id: "ir2", type: "informational", title: "Instructions", text: "36 questions in the passages that follow, certain words and phrases and underlines and highlighted...", clickable: true, answered: false, flagged: false},
    ],
    scienceAnswers: [
        {id: "is1", type: "informational", title: "Information", text: "Science Test Directions. The next screen in this section contains instructions about the Science test...", clickable: false, answered: false, flagged: false},
        {id: "is2", type: "informational", title: "Instructions", text: "40 questions. There are several passages in this test. Each passage is followed by several questions. After reading a passage, choose the best ...", clickable: true, answered: false, flagged: false},
    ],
}

interface ISectionRightProps {
    questions: any[]
    showSplitPanel: boolean
    currentInformationalIndex: number
    currentQuestionIndex: number
    onToggleFilteredArea: () => void
    onGotoQuestion: (questionNumber: number) => void
    onGotoInformation: (item) => void
}

interface ISectionRightState {
    allItems: any[]
    selectedItems: any[]
    selectedFilter: string
}

// This is the right side with the icon ☰ that toggles the index section
class SectionRight extends Component<ISectionRightProps, ISectionRightState> {
    constructor(props: ISectionRightProps) {
        super (props)
        this.state = {
            allItems: [],
            selectedItems: [],
            selectedFilter: "allquestions"
        }
    }

    componentDidMount(): void {
        let {questions} = this.props
        this.init(questions)
    }

    componentDidUpdate(prevProps: ISectionRightProps): void {
        if (prevProps.questions !== this.props.questions) {
            this.init(this.props.questions)
        }
    }

    init = (questions) => {
        let sectionKey = questions?.[0]?.section ?? "?"
        let itemsI = informationalItemsBySections[sectionKey]
        let itemsQ = questions.map(question => {
            let text = question.questionQualifier
            return {
                id: `q${question.questionNumber}`,
                type: "question",
                title: `Question ${question.questionNumber}`,
                text,
                answered: question.userSubmittedAnswer && question.userSubmittedAnswer !== "?",
                flagged: question.bookmarked,
                clickable: true,
                ...question
            }
        })

        let allItems = [...itemsI, ...itemsQ]
        let {selectedFilter} = this.state
        let option = optionsMap[selectedFilter] ?? optionsMap["allquestions"]
        let selectedItems = option.getQuestions(allItems)
        this.setState({allItems, selectedItems})
    }

    toggleRightSection = () => {
        let {onToggleFilteredArea} = this.props

        if (onToggleFilteredArea) {
            onToggleFilteredArea()
        }
    }

    handleFilter = (filterValue) => {
        let {allItems} = this.state
        let option = optionsMap[filterValue] ?? optionsMap["allquestions"]
        let selectedItems = option.getQuestions(allItems)
        this.setState({selectedItems, selectedFilter: filterValue})
    }

    getOptionInfo = (filterValue) => {
        let {questions} = this.props
        let option = optionsMap[filterValue] ?? optionsMap["allquestions"]
        return option.info(questions)
    }

    handleQuestionClick(e, question) {
        let {onGotoInformation, onGotoQuestion} = this.props
        let {id, type, clickable} = question

        if (type === "informational") {
            if (onGotoInformation && clickable) {
                onGotoInformation(question) // i.e. item
            }
            return
        }

        if (onGotoQuestion) {
            onGotoQuestion(question.questionNumber)
        }
    }

    render() {
        let {showSplitPanel, currentInformationalIndex, currentQuestionIndex} = this.props
        let {selectedFilter, selectedItems} = this.state
        let indexButtonClassName = `index-button ${showSplitPanel ? "selected": ""}`
        let currentFilterOptionDisplayText = optionsMap[selectedFilter].label
        let filterOptions = Object.keys(optionsMap)

        return (
            <div className="section-right">
            { showSplitPanel &&
                <div className="filtered-area">
                    <div className="filtered-area--title">
                        <IconFilter/>
                        {" "}
                        Filter By
                        <ActFilterDropdown label={currentFilterOptionDisplayText}>
                            {filterOptions.map(option => {
                                let selected = option === selectedFilter
                                let optionInfo = this.getOptionInfo(option)
                                return (
                                    <button
                                        key={option}
                                        className="act-filter-dropwdown-item"
                                        onClick={() => this.handleFilter(option)}>
                                        <span className="act-filter-dropdown-item--ico">
                                            <img src="/assets/images/icons/v2/ico-check-tool.svg" alt="" width="16" height="16" className={selected ? "" : "disabled"}/>
                                        </span>
                                        {optionInfo}
                                    </button>
                                )
                            })}
                        </ActFilterDropdown>
                    </div>
                    <div className="filtered-cards-container">
                        <div id="filteredcards" className="filtered-cards">
                            {
                                selectedItems.map(item => 
                                    <FilteredItem
                                        key={item.id}
                                        item={item}
                                        question={item}
                                        informationClicked={false} // OT: Remove this
                                        currentInformationalIndex={currentInformationalIndex}
                                        currentQuestionIndex={currentQuestionIndex}
                                        onClick={(e) => this.handleQuestionClick(e, item)}
                                    />
                                )
                            }
                        </div>
                    </div>
                </div>
            }

            <div>
                <button className={indexButtonClassName} onClick={this.toggleRightSection}>
                    <svg fill="currentColor" width="20" height="20" xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512'><path d='M0 96C0 78.3 14.3 64 32 64H416c17.7 0 32 14.3 32 32s-14.3 32-32 32H32C14.3 128 0 113.7 0 96zM0 256c0-17.7 14.3-32 32-32H416c17.7 0 32 14.3 32 32s-14.3 32-32 32H32c-17.7 0-32-14.3-32-32zM448 416c0 17.7-14.3 32-32 32H32c-17.7 0-32-14.3-32-32s14.3-32 32-32H416c17.7 0 32 14.3 32 32z'/></svg>
                    <div className="index-label">
                        INDEX
                    </div>
                </button>

                {/* <div className="icon-bars-container network-online">
                    <IconBars />
                </div> */}
            </div>
        </div>
        )
    }

}

function ActTopInfo({remainingTimeInSeconds, startTimer, data, onTimerTick, onTimerFinished}: {remainingTimeInSeconds: number, startTimer: boolean, data: any, onTimerTick: () => void, onTimerFinished?: () => void}) {
    let [showTime, setShowTime] = useState(true)
    let {userName, questionsAnswered, questionCurrent, questionMax} = data
    let nofQuestionsAnswered = questionsAnswered
    let nofQuestionsTotal = questionMax
    let questionsInfo = `${nofQuestionsAnswered} of ${nofQuestionsTotal}`
    let timeInfo = <CountdownTimerACT startTimer={startTimer} fromInSeconds={remainingTimeInSeconds} showTime={showTime} onTimerTick={onTimerTick} onFinish={onTimerFinished}/>

    let timeVisibilityIcon = showTime ? <IconEyeClosed/>: <IconEyeOpen/>

    function toggleTimeVisibility() {
        setShowTime(!showTime)
    }

    function endTest() {
        ComputerTestACT.showEndOfTestModal()
    }

    return (
        <div className="col eact-col-data pr-0">
            <div className="eact-test-controls">
                <span className="topInfoLabel">ANSWERED</span>
                <span className="answeredInfo">
                    {questionsInfo}
                </span>
                <span className="topInfoLabel">TOTAL TIME LEFT</span>
                <span className="timeInfo">
                    {timeInfo}
                </span>
                <ActButton btnClass="eact-control-hide" label="Hide Timer" labelHidden icon={timeVisibilityIcon} onClick={toggleTimeVisibility}/>
                <ActButton btnClass="eact-control-end" label="END TEST" icon={<IconStop/>} onClick={endTest}/>
            </div>
        </div>
    )
}

function SectionTop(props) {
    let {currentModuleNameKey, sectionTitle, testTitle: testInfo, remainingTimeInSeconds, startTimer, 
        onToggleCalculator, onToggleReference, onToggleDirections, onToggleLineReader, onToggleHighlight, onTimerTick, onTimeFinish, onTestExit,
        computerTestCapabilities, isDirectionsOpen, 
        data, onCommand} = props
    let durationInSeconds = remainingTimeInSeconds
    let {userName, questionCurrent, questionMax} = data

    return (
        <div className="section-top row align-items-center mx-0">
            <ActLogo />
            <ActTopCenterControls computerTestCapabilities={computerTestCapabilities} onCommand={onCommand}/>
            <ActTopInfo 
                remainingTimeInSeconds={remainingTimeInSeconds} 
                startTimer={startTimer} 
                data={data} 
                onTimerTick={onTimerTick} 
                onTimerFinished={onTimeFinish}
            />
        </div>
    )
}

function SectionMain(props) {
    let { children } = props

    return (
        <div className="section-main">
            {children}
        </div>
    )
}

function SectionMiddleContainer(props) {
    let { children } = props

    return (
        <div className="section-middle-container">
            {children}
        </div>
    )
}

function SectionQuestionArea(props) {
    let { showInformation, 
        questions=[], questionIndex=0, highlighterEnabled,
        onAnswer, onBookmark, onHighlight, 
        computerTestCapabilities,
        onBack, onNext, 
        disableBack, disableNext, currentInformationalIndex } = props
    let {highlight} = computerTestCapabilities
    let question = questions[questionIndex] as IAnsweredQuestion
    let section = question.section
    let showInformationalContent = currentInformationalIndex < NUMBER_OF_INFORMATIONAL_PAGES

    if (showInformationalContent && currentInformationalIndex <= 1) {
        disableBack = true
    }
    else {
        disableBack = false
    }

    return (
        showInformationalContent ?
            <InformationalContent
                currentInformationalIndex={currentInformationalIndex} 
                showInformation={showInformation} 
                section={section}
                allowBookmark={false}
                onBookmark={onBookmark}
                onBack={onBack}
                onNext={onNext}
                disableBack={disableBack}
                disableNext={disableNext}
            />
            :
            <QuestionACT
                question={question}
                totalQuestionsCount={questions.length}
                highlighterEnabled={highlight && highlighterEnabled}
                onAnswer={onAnswer}
                onBookmark={onBookmark}
                onHighlight={onHighlight}
                onBack={onBack}
                onNext={onNext}
                disableBack={disableBack}
                disableNext={disableNext}
            />
    )
}

/**
 * Get the test questions stats
 */
function getQuestionsStats(questions) {
    let questionsFlagged = questions.filter(q => q.bookmarked)
    let questionsUnanswered = questions.filter(q => !q.userSubmittedAnswer)
    let nofQuestionsFlagged = questionsFlagged.length
    let nofQuestionsUnanswered = questionsUnanswered.length

    return {nofQuestionsFlagged, nofQuestionsUnanswered}
}

function EndOfTestConfirmation(props) {
    let {title="End of Test", questions, onClose, onEndTest, onNextSection} = props
    let {nofQuestionsFlagged, nofQuestionsUnanswered} = getQuestionsStats(questions)

    return (
        <div className="act-blurred-glass-modal">
            <div className="act-toast">
                <div className="act-toast-header">
                    <h2 id="dlg-title" className="act-toast-title">{title}</h2>
                    <button className="act-toast-close-btn" onClick={onClose}>✕</button>
                </div>
                <div className="act-toast-body">
                    <p>
                        There are <b>{nofQuestionsUnanswered}</b> unanswered question(s).
                        <button className="act-toast-review-btn">UNANSWERED REVIEW</button>
                    </p>
                    <p>
                        There are <b>{nofQuestionsFlagged}</b> question(s) flagged for review.
                        <button className="act-toast-review-btn">FLAGGED REVIEW</button>
                    </p>
                     <p>
                        Are you sure you want to end the test? You will not be able to resume this module.
                    </p>
                </div>
                <div className="act-toast-footer">
                    <button className="btn-test-end" onClick={onEndTest}>SAVE AND EXIT</button>
                    <button className="btn-test-next" onClick={onNextSection}>PROCEED TO NEXT SECTION</button>
                    <button className="btn-test-close" onClick={onClose}>GO BACK TO SECTION</button>
                </div>
            </div>

        </div>
    )
}

function EndOfTestReview(props) {
    let {title="End of Test", questions, onClose, onEndTest, onNextSection} = props
    let {nofQuestionsFlagged, nofQuestionsUnanswered} = getQuestionsStats(questions)

    return (
        <div className="act-blurred-glass-modal">
            <div className="act-toast">
                <div className="act-toast-header">
                    <h2 id="dlg-title" className="act-toast-title">{title}</h2>
                    <button className="act-toast-close-btn" onClick={onClose}>✕</button>
                </div>
                <div className="act-toast-body">
                    <p>
                        There are <b>{nofQuestionsUnanswered}</b> unanswered question(s).
                        <button className="act-toast-review-btn">UNANSWERED REVIEW</button>
                    </p>
                    <p>
                        There are <b>{nofQuestionsFlagged}</b> question(s) flagged for review.
                        <button className="act-toast-review-btn">FLAGGED REVIEW</button>
                    </p>
                     <p>
                        Are you sure you want to end the test? You will not be able to resume this module.
                    </p>
                </div>
                <div className="act-toast-footer">
                    <button className="btn-test-end" onClick={onNextSection}>PROCEED TO NEXT SECTION</button>
                    <button className="btn-test-close" onClick={onClose}>GO BACK TO SECTION</button>
                </div>
            </div>

        </div>
    )
}

//#endregion